import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

/**
 * Stream Sharing Hook - Manages shared stream sessions and viewer tracking
 * Integrates with backend session management for efficient resource sharing
 */
const useStreamSharing = () => {
  const [activeSessions, setActiveSessions] = useState(new Map()); // cameraId -> sessionData
  const [sessionHeartbeats, setSessionHeartbeats] = useState(new Map()); // sessionId -> heartbeatData
  const [viewerCounts, setViewerCounts] = useState(new Map()); // cameraId -> viewerCount
  const [sessionStats, setSessionStats] = useState({
    totalViewers: 0,
    activeStreams: 0,
    efficiency: 0
  });

  const heartbeatInterval = useRef(null);
  const sessionId = useRef(null);
  const isInitialized = useRef(false);

  // Generate unique session ID for this client
  useEffect(() => {
    if (!sessionId.current) {
      sessionId.current = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log(`🔗 Stream sharing session initialized: ${sessionId.current}`);
    }
  }, []);

  // Start heartbeat system
  const startHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) return;

    heartbeatInterval.current = setInterval(async () => {
      try {
        // Send heartbeat for all active sessions
        const activeCameraIds = Array.from(activeSessions.keys());
        if (activeCameraIds.length > 0) {
          await api.post('/streams/heartbeat', {
            sessionId: sessionId.current,
            cameraIds: activeCameraIds,
            timestamp: Date.now()
          });
        }
      } catch (error) {
        console.warn('Heartbeat failed:', error);
      }
    }, 30000); // 30 seconds

    console.log('💓 Stream sharing heartbeat started');
  }, [activeSessions]);

  // Stop heartbeat system
  const stopHeartbeat = useCallback(() => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
      console.log('💓 Stream sharing heartbeat stopped');
    }
  }, []);

  // Join a camera stream session
  const joinStreamSession = useCallback(async (cameraId, userData = {}) => {
    if (!sessionId.current) {
      console.error('Session ID not initialized');
      return false;
    }

    try {
      console.log(`👥 Joining stream session for camera ${cameraId}`);
      
      const response = await api.post('/streams/join', {
        cameraId,
        sessionId: sessionId.current,
        userData: {
          ...userData,
          userId: userData.userId || 'anonymous',
          clientInfo: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screenResolution: `${screen.width}x${screen.height}`,
            timestamp: Date.now()
          }
        }
      });

      if (response.data.success) {
        const { viewerCount, sessionData } = response.data.data;
        
        // Update local state
        setActiveSessions(prev => new Map(prev).set(cameraId, {
          ...sessionData,
          joinTime: Date.now(),
          lastHeartbeat: Date.now()
        }));
        
        setViewerCounts(prev => new Map(prev).set(cameraId, viewerCount));
        
        // Start heartbeat if not already running
        if (!heartbeatInterval.current) {
          startHeartbeat();
        }

        console.log(`✅ Joined stream session for camera ${cameraId} (${viewerCount} viewers)`);
        return true;
      }
    } catch (error) {
      console.error(`❌ Failed to join stream session for camera ${cameraId}:`, error);
    }
    
    return false;
  }, [startHeartbeat]);

  // Leave a camera stream session
  const leaveStreamSession = useCallback(async (cameraId) => {
    if (!sessionId.current) return;

    try {
      console.log(`👋 Leaving stream session for camera ${cameraId}`);
      
      const response = await api.post('/streams/leave', {
        cameraId,
        sessionId: sessionId.current
      });

      if (response.data.success) {
        const { viewerCount } = response.data.data;
        
        // Update local state
        setActiveSessions(prev => {
          const newMap = new Map(prev);
          newMap.delete(cameraId);
          return newMap;
        });
        
        setViewerCounts(prev => new Map(prev).set(cameraId, viewerCount));
        
        // Stop heartbeat if no active sessions
        if (activeSessions.size <= 1) {
          stopHeartbeat();
        }

        console.log(`✅ Left stream session for camera ${cameraId} (${viewerCount} viewers remaining)`);
        return true;
      }
    } catch (error) {
      console.error(`❌ Failed to leave stream session for camera ${cameraId}:`, error);
    }
    
    return false;
  }, [activeSessions.size, stopHeartbeat]);

  // Get viewer count for a camera
  const getViewerCount = useCallback((cameraId) => {
    return viewerCounts.get(cameraId) || 0;
  }, [viewerCounts]);

  // Check if user is viewing a camera
  const isViewingCamera = useCallback((cameraId) => {
    return activeSessions.has(cameraId);
  }, [activeSessions]);

  // Get session data for a camera
  const getSessionData = useCallback((cameraId) => {
    return activeSessions.get(cameraId) || null;
  }, [activeSessions]);

  // Update session stats
  const updateSessionStats = useCallback(async () => {
    try {
      const response = await api.get('/streams/stats');
      if (response.data.success) {
        setSessionStats(response.data.data);
      }
    } catch (error) {
      console.warn('Failed to update session stats:', error);
    }
  }, []);

  // Get sharing efficiency metrics
  const getSharingEfficiency = useCallback(() => {
    const totalViewers = Array.from(viewerCounts.values()).reduce((sum, count) => sum + count, 0);
    const activeStreams = activeSessions.size;
    const efficiency = activeStreams > 0 ? Math.round((totalViewers / activeStreams) * 100) / 100 : 0;
    
    return {
      totalViewers,
      activeStreams,
      efficiency,
      viewersPerStream: efficiency
    };
  }, [viewerCounts, activeSessions.size]);

  // Cleanup all sessions on unmount
  const cleanupAllSessions = useCallback(async () => {
    if (!sessionId.current) return;

    try {
      console.log('🧹 Cleaning up all stream sessions');
      
      const activeCameraIds = Array.from(activeSessions.keys());
      if (activeCameraIds.length > 0) {
        await api.post('/streams/cleanup', {
          sessionId: sessionId.current,
          cameraIds: activeCameraIds
        });
      }
      
      // Clear local state
      setActiveSessions(new Map());
      setViewerCounts(new Map());
      setSessionHeartbeats(new Map());
      
      // Stop heartbeat
      stopHeartbeat();
      
      console.log('✅ All stream sessions cleaned up');
    } catch (error) {
      console.error('❌ Failed to cleanup stream sessions:', error);
    }
  }, [activeSessions, stopHeartbeat]);

  // Initialize stream sharing
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      console.log('🚀 Stream sharing hook initialized');
      
      // Update stats periodically
      const statsInterval = setInterval(updateSessionStats, 60000); // Every minute
      
      return () => {
        clearInterval(statsInterval);
        cleanupAllSessions();
      };
    }
  }, [updateSessionStats, cleanupAllSessions]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 Page hidden - pausing heartbeat');
        stopHeartbeat();
      } else {
        console.log('📱 Page visible - resuming heartbeat');
        if (activeSessions.size > 0) {
          startHeartbeat();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeSessions.size, startHeartbeat, stopHeartbeat]);

  // Handle beforeunload to cleanup sessions
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable cleanup on page unload
      const activeCameraIds = Array.from(activeSessions.keys());
      if (activeCameraIds.length > 0 && sessionId.current) {
        const data = JSON.stringify({
          sessionId: sessionId.current,
          cameraIds: activeCameraIds
        });
        
        navigator.sendBeacon('/api/streams/cleanup', data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeSessions]);

  return {
    // Session management
    joinStreamSession,
    leaveStreamSession,
    
    // State queries
    getViewerCount,
    isViewingCamera,
    getSessionData,
    
    // Statistics
    getSharingEfficiency,
    sessionStats,
    
    // Utilities
    sessionId: sessionId.current,
    activeSessionsCount: activeSessions.size,
    totalViewers: Array.from(viewerCounts.values()).reduce((sum, count) => sum + count, 0),
    
    // Cleanup
    cleanupAllSessions
  };
};

export default useStreamSharing;
